import axiosInstance from '../utils/api';

/**
 * Translate content with streaming support
 * Uses the same endpoint for both blog and product translation
 * @param {Object} params - Translation parameters
 * @param {Object} params.jsonData - The content to translate (same format will be returned)
 * @param {string} params.sourceLanguage - Source language (default: "English")
 * @param {string} params.targetLanguage - Target language
 * @param {string} params.targetRegion - Target region (default: "Global")
 * @param {string} params.sessionId - Optional session ID
 * @param {Function} params.onChunk - Callback for each chunk (chunk, accumulatedData)
 * @param {Function} params.onComplete - Callback when translation is complete (finalData)
 * @param {Function} params.onError - Callback for errors
 */
export const translateContent = async ({
  jsonData,
  sourceLanguage = "English",
  targetLanguage,
  targetRegion = "Global",
  sessionId = null,
  onChunk,
  onComplete,
  onError,
}) => {
  try {
    const token = localStorage.getItem('token');
    if (!token) {
      const error = new Error('Authentication required. Please log in.');
      if (onError) onError(error);
      throw error;
    }

    if (!targetLanguage) {
      const error = new Error('Target language is required');
      if (onError) onError(error);
      throw error;
    }

    // Build request body
    const requestBody = {
      request_type: "language_translate",
      streaming: true,
      additional_data: {
        json_data: jsonData,
        source_language: sourceLanguage,
        target_language: targetLanguage,
        target_region: targetRegion,
      },
    };

    // Add session_id if provided
    if (sessionId) {
      requestBody.session_id = sessionId;
    }

    // Call the translation endpoint
    const response = await fetch(`${axiosInstance.defaults.baseURL}/generates/translate_product_content`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'Accept': 'text/event-stream',
        'Authorization': `Bearer ${token}`,
      },
      body: JSON.stringify(requestBody),
    });

    if (!response.ok) {
      const errorText = await response.text();
      console.error('Translation error response:', errorText);
      const error = new Error(`Translation API error: ${response.status} - ${errorText}`);
      if (onError) onError(error);
      throw error;
    }

    // Handle streaming response
    const reader = response.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';
    let accumulatedData = '';
    let lastProcessedIndex = 0;
    let isProcessingChunk = false;

    // Helper function to extract complete JSON objects from fragmented data
    const extractCompleteJsonObjects = (data) => {
      const jsonObjects = [];
      let braceCount = 0;
      let startIndex = -1;
      let currentIndex = 0;
      
      while (currentIndex < data.length) {
        const char = data[currentIndex];
        
        if (char === '{') {
          if (braceCount === 0) {
            startIndex = currentIndex;
          }
          braceCount++;
        } else if (char === '}') {
          braceCount--;
          if (braceCount === 0 && startIndex !== -1) {
            const jsonString = data.substring(startIndex, currentIndex + 1);
            if (jsonString.trim().length > 2 && jsonString.includes(':') && jsonString.includes('"')) {
              jsonObjects.push({
                json: jsonString,
                startIndex: startIndex,
                endIndex: currentIndex
              });
            }
            startIndex = -1;
          }
        }
        
        currentIndex++;
      }
      
      return jsonObjects;
    };

    // Helper function to clean Python-specific values
    const cleanPythonValues = (str) => {
      return str
        .replace(/:\s*}/g, ': ""}')
        .replace(/:\s*None\b/g, ': null')
        .replace(/,\s*None\b/g, ', null')
        .replace(/\[\s*None\s*\]/g, '[null]')
        .replace(/\{\s*None\s*:\s*[^}]*\}/g, '{}')
        .replace(/:\s*True\b/g, ': true')
        .replace(/:\s*False\b/g, ': false')
        .replace(/,\s*True\b/g, ', true')
        .replace(/,\s*False\b/g, ', false');
    };

    try {
      while (true) {
        const { done, value } = await reader.read();
        
        if (done) {
          break;
        }

        // Decode the chunk and add to buffer
        buffer += decoder.decode(value, { stream: true });
        
        // Process complete lines
        const lines = buffer.split('\n');
        buffer = lines.pop() || ''; // Keep incomplete line in buffer
        
        for (const line of lines) {
          if (line.startsWith('data: ')) {
            const data = line.slice(6); // Remove 'data: ' prefix
            
            if (data.trim() === '' || data.trim() === '[DONE]') continue; // Skip empty lines and done markers
            
            // Accumulate all data
            accumulatedData += data;
          }
        }

        // Try to process accumulated data for complete JSON objects in real-time
        if (!isProcessingChunk && accumulatedData.length > lastProcessedIndex) {
          isProcessingChunk = true;
          
          try {
            // Look for complete JSON objects in the new accumulated data
            const newData = accumulatedData.substring(lastProcessedIndex);
            const jsonObjects = extractCompleteJsonObjects(newData);
            
            for (const jsonObj of jsonObjects) {
              try {
                // Clean Python-specific values
                const cleanedJson = cleanPythonValues(jsonObj.json);
                const parsed = JSON.parse(cleanedJson);
                
                // Call onChunk with parsed data (preferred - parsed objects)
                // This will override/update the UI with structured data
                if (onChunk && typeof onChunk === 'function') {
                  onChunk(parsed, accumulatedData);
                }
                
                lastProcessedIndex = lastProcessedIndex + jsonObj.endIndex + 1;
              } catch (parseError) {
                // Try to fix common JSON issues
                try {
                  let fixedJson = jsonObj.json
                    .replace(/,\s*}/g, '}')
                    .replace(/,\s*]/g, ']')
                    .replace(/:\s*}/g, ': ""}')
                    .replace(/:\s*None\b/g, ': null')
                    .replace(/:\s*True\b/g, ': true')
                    .replace(/:\s*False\b/g, ': false');
                  
                  fixedJson = cleanPythonValues(fixedJson);
                  const parsed = JSON.parse(fixedJson);
                  
                  if (onChunk) {
                    onChunk(parsed, accumulatedData);
                  }
                  
                  lastProcessedIndex = lastProcessedIndex + jsonObj.endIndex + 1;
                } catch (fixError) {
                  console.warn('Failed to parse JSON chunk:', fixError);
                  // Continue to next JSON object
                }
              }
            }
          } catch (error) {
            console.warn('Error processing streaming translation chunk:', error);
          } finally {
            isProcessingChunk = false;
          }
        }
      }
    } finally {
      reader.releaseLock();
    }

    // Process any remaining data after stream completes
    let finalTranslatedData = null;
    
    try {
      // Remove all "data: " prefixes and combine the content
      const lines = accumulatedData.split('\n');
      let cleanedData = '';
      
      for (const line of lines) {
        if (line.startsWith('data: ')) {
          cleanedData += line.substring(6);
        } else if (line.trim() !== '' && !line.includes('[DONE]')) {
          cleanedData += line;
        }
      }
      
      if (cleanedData.trim()) {
        // Try to extract complete JSON objects from remaining data
        const jsonObjects = extractCompleteJsonObjects(cleanedData);
        
        // Get the last complete JSON object (most complete translation)
        if (jsonObjects.length > 0) {
          const lastJsonObj = jsonObjects[jsonObjects.length - 1];
          try {
            const cleanedJson = cleanPythonValues(lastJsonObj.json);
            finalTranslatedData = JSON.parse(cleanedJson);
          } catch (parseError) {
            // Try to parse the entire cleaned data
            try {
              const cleanedJson = cleanPythonValues(cleanedData);
              finalTranslatedData = JSON.parse(cleanedJson);
            } catch (finalParseError) {
              console.warn('Failed to parse final translation data:', finalParseError);
              // Return the cleaned data as-is if parsing fails
              finalTranslatedData = cleanedData;
            }
          }
        } else {
          // Try to parse the entire cleaned data
          try {
            const cleanedJson = cleanPythonValues(cleanedData);
            finalTranslatedData = JSON.parse(cleanedJson);
          } catch (parseError) {
            console.warn('Failed to parse translation data:', parseError);
            finalTranslatedData = cleanedData;
          }
        }
      }
    } catch (error) {
      console.error('Error processing final translation data:', error);
      finalTranslatedData = accumulatedData;
    }

    // Call onComplete callback with final data
    if (onComplete) {
      onComplete(finalTranslatedData || accumulatedData);
    }

    return finalTranslatedData || accumulatedData;
  } catch (error) {
    console.error('Translation error:', error);
    if (onError) {
      onError(error);
    }
    throw error;
  }
};

export default translateContent;
